import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem, SelectSeparator } from '@/components/ui/select';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { Badge, type BadgeProps } from '@/components/badges/Badge/Badge';
import IconArrowRight from '@/assets/icons/icon-arrow-right-02.svg?react';
import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import IconSpinner from '@/assets/icons/icon-spiner.svg?react';
import IconCheckmarkCircleFill from '@/assets/icons/icon-checkmark-circle-fill.svg?react';
import IconInternet from '@/assets/icons/icon-internet.svg?react';
import IconCopy from '@/assets/icons/icon-copy.svg?react';
import IconLinkSquare from '@/assets/icons/icon-link-square.svg?react';
import IconGift from '@/assets/icons/icon-gift.svg?react';
import IconSettings from '@/assets/icons/icon-settings.svg?react';
import IconWifiOff from '@/assets/icons/icon-wifi-off.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { unpublishPage } from '@/store/pages/thunks';
import { useParams } from 'react-router-dom';
import {
  selectCurrentPage,
  selectPublishStatus,
  selectPublishedUrl,
  selectSelectedPath,
  selectUnpublishing,
} from '@/store/pages/selectors';
import { setSelectedPath } from '@/store/pages/slice';
import { selectDomains, selectSelectedDomain } from '@/store/domains/selectors';
import { setSelectedDomain } from '@/store/domains/slice';
import { selectCanRedeemDomainVoucher, selectMaxDomains } from '@/store/user/selectors';
import { convertToSlug } from '@/utils/slugHelper';
import { pathValidation } from '@builder4/shared/utils/regex';
import type { DomainChipStatus } from '@/store/domains/types';

const chipStatusToBadge: Record<DomainChipStatus, { text: string; color: BadgeProps['color'] }> = {
  default: { text: 'DEFAULT', color: 'muted' },
  connected: { text: 'CONNECTED', color: 'success' },
  pending: { text: 'PENDING', color: 'warning' },
  error: { text: 'ERROR', color: 'error' },
};

interface PublishControlPanelProps {
  className?: string;
  onClose?: () => void;
  onPublish: () => void;
  publishButtonText: string;
}

export const PublishControlPanel: React.FC<PublishControlPanelProps> = ({
  className = '',
  onClose,
  onPublish,
  publishButtonText,
}) => {
  const dispatch = useAppDispatch();
  const domainList = useAppSelector(selectDomains);
  const selectedDomain = useAppSelector(selectSelectedDomain);
  const currentPage = useAppSelector(selectCurrentPage);
  const publishingStatus = useAppSelector(selectPublishStatus);
  const canRedeemDomainVoucher = useAppSelector(selectCanRedeemDomainVoucher);
  const maxDomains = useAppSelector(selectMaxDomains);
  const customDomainsCount = domainList.length - 1; // Lpages domain removed from count
  const showFreeDomain = canRedeemDomainVoucher && customDomainsCount < maxDomains;
  const publishedUrl = useAppSelector(selectPublishedUrl);
  const selectedPath = useAppSelector(selectSelectedPath);
  const unpublishing = useAppSelector(selectUnpublishing);
  const { uuid } = useParams<{ uuid: string }>();

  const [currentPath, setCurrentPath] = useState(currentPage?.path ?? '');
  const [isSlugValid, setIsSlugValid] = useState(true);

  const isPublished = !!currentPage?.publishedVersionId;
  const hasSlugError = !isSlugValid && currentPath !== '';

  // Default slug to page title when empty
  useEffect(() => {
    if (!selectedPath && !currentPath && currentPage?.name) {
      const slug = convertToSlug(currentPage.name);
      setCurrentPath(slug);
      dispatch(setSelectedPath(slug));
    }
  }, [currentPage?.name, selectedPath, currentPath]);

  // Sync path from Redux
  useEffect(() => {
    const newPath = selectedPath ?? currentPage?.path ?? '';
    if (newPath !== currentPath) {
      setCurrentPath(newPath);
      setIsSlugValid(newPath === '' || pathValidation.test(newPath));
    }
  }, [selectedPath, currentPage?.path]);

  // Debounce Redux updates
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (currentPath !== selectedPath) {
        dispatch(setSelectedPath(currentPath));
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [currentPath]);

  const handleValidateCurrentPath = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const isValid = value === '' || pathValidation.test(value);
    setCurrentPath(value);
    setIsSlugValid(isValid);
  };

  const handleUnpublish = async () => {
    if (!uuid || unpublishing) return;
    await dispatch(unpublishPage({ pageId: uuid }));
  };

  const handleCopyUrl = () => {
    if (publishedUrl) {
      navigator.clipboard.writeText(publishedUrl);
    }
  };

  const handleOpenInNewTab = () => {
    if (publishedUrl) {
      window.open(publishedUrl, '_blank');
    }
  };

  const handleManageDomains = () => {
    const mainAppUrl = import.meta.env.VITE_API_MAIN_APP;
    window.open(`${mainAppUrl}/#/domains`, '_blank');
  };

  const handleClaimFreeDomain = () => {
    const mainAppUrl = import.meta.env.VITE_API_MAIN_APP;
    window.open(`${mainAppUrl}/#/domains`, '_blank');
  };

  const domainHasChanged =
    isPublished && selectedDomain && currentPage?.domain && selectedDomain !== currentPage.domain;
  const pathHasChanged =
    isPublished && currentPath && currentPage?.path && currentPath !== currentPage.path;
  const showRepublishHint = domainHasChanged || pathHasChanged;

  const currentDomain = selectedDomain || currentPage?.domain || '';
  const currentUrl = currentDomain && currentPath ? `https://${currentDomain}/${currentPath}` : '';
  const isCurrentUrlLive = isPublished && currentUrl === publishedUrl;

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex w-[280px] flex-col items-stretch gap-3 rounded-lg border border-neutral-100 pb-3 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)]',
        className,
      )}
    >
      {/* Header */}
      <div className="bg-neutral-0 inline-flex h-10 items-center justify-between rounded-t-lg border-b border-neutral-100 pr-2 pl-3">
        <span className="text-xs leading-none font-medium text-neutral-900">Publish</span>
        <IconSimpleButton
          icon={IconCancel}
          size={24}
          state="default"
          onClick={onClose}
          data-qa-selector="publish-panel-close-button"
        />
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 px-3">
        {/* Domain dropdown */}
        <SelectField
          text=""
          size="full"
          className="w-full"
          classNameTrigger="w-full max-w-none"
          icon={IconInternet}
          defaultValue={selectedDomain || currentPage?.domain || ''}
          placeholder="Select a domain"
          onValueChange={(value) => dispatch(setSelectedDomain(value))}
          data-qa-selector="publish-panel-domain-select"
        >
          <div className="max-h-[180px] overflow-y-auto">
            {domainList.map((item) => {
              const badge = chipStatusToBadge[item.chipStatus];
              return (
                <SelectItem value={item.domain} key={item.domain} disabled={!item.isSelectable}>
                  <div className="flex items-center gap-2">
                    <IconInternet className="h-4 w-4 shrink-0 text-neutral-500" />
                    <span className="truncate">{item.domain}</span>
                    <Badge text={badge.text} color={badge.color} size="small" />
                  </div>
                </SelectItem>
              );
            })}
          </div>
          <SelectSeparator />
          {showFreeDomain && (
            <div
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-xs font-medium hover:bg-neutral-100"
              onPointerDown={(e) => {
                e.preventDefault();
                handleClaimFreeDomain();
              }}
              data-qa-selector="publish-panel-claim-free-domain"
            >
              <IconGift className="h-4 w-4 shrink-0" />
              <span>Claim Free Domain</span>
              <span className="inline-flex items-center justify-center rounded border border-green-500 bg-green-100 px-2 py-0.5 text-[10px] leading-none font-semibold text-green-500">
                FREE
              </span>
            </div>
          )}
          <div
            className="flex cursor-pointer items-center gap-2 rounded p-2 text-xs font-medium hover:bg-neutral-100"
            onPointerDown={(e) => {
              e.preventDefault();
              handleManageDomains();
            }}
          >
            <IconSettings className="h-4 w-4 shrink-0" />
            <span>Manage Domain Settings</span>
            <IconArrowRight className="h-4 w-4 shrink-0" />
          </div>
        </SelectField>

        {/* Page Slug */}
        <TextField
          className="w-full gap-2"
          inputValue={currentPath}
          placeholderText="about-me"
          onChange={handleValidateCurrentPath}
          state="default"
          text="Page Slug"
          type="text"
          errorState={hasSlugError}
          data-qa-selector="publish-panel-slug-input"
        />
        {hasSlugError && (
          <small className="text-destructive -mt-2 text-[10px]">Invalid slug format</small>
        )}

        {/* Status card */}
        <div className="rounded-md border border-neutral-100 p-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'h-2 w-2 rounded-full',
                  isCurrentUrlLive ? 'bg-green-500' : 'bg-neutral-300',
                )}
              />
              <span
                className={clsx(
                  'text-xs font-medium',
                  isCurrentUrlLive ? 'text-neutral-900' : 'text-neutral-500',
                )}
              >
                {isCurrentUrlLive ? 'Live page' : 'Page is not live'}
              </span>
            </div>
            {currentUrl && (
              <span className="text-[10px] break-all text-neutral-400">{currentUrl}</span>
            )}
            {isPublished && (
              <div className="flex items-center gap-1">
                <IconSimpleButton
                  icon={IconCopy}
                  tooltipContent="Copy URL"
                  size={28}
                  state="default"
                  outlined
                  onClick={handleCopyUrl}
                  data-qa-selector="publish-panel-copy-url-button"
                />
                <IconSimpleButton
                  icon={IconLinkSquare}
                  tooltipContent="View page"
                  size={28}
                  state={isCurrentUrlLive ? 'default' : 'disabled'}
                  outlined
                  onClick={handleOpenInNewTab}
                  data-qa-selector="publish-panel-view-page-button"
                />
                <SecondaryButton
                  icon={IconWifiOff}
                  size="sm"
                  state={isCurrentUrlLive ? (unpublishing ? 'loading' : 'default') : 'disabled'}
                  text={unpublishing ? 'Unpublishing...' : 'Unpublish'}
                  onClick={handleUnpublish}
                  className="ml-auto"
                  data-qa-selector="publish-panel-unpublish-button"
                />
              </div>
            )}
          </div>
        </div>

        {/* Publish/Republish button */}
        <div className="flex flex-col gap-1">
          {publishingStatus === 'idle' && (
            <PrimaryButton
              icon={null}
              size="sm"
              state="default"
              text={publishButtonText}
              className="h-8 w-full"
              onClick={onPublish}
              data-qa-selector="publish-panel-submit-button"
            />
          )}
          {publishingStatus === 'publishing' && (
            <PrimaryButton
              icon={IconSpinner}
              size="sm"
              state="loading"
              text={publishButtonText}
              className="h-8 w-full"
              data-qa-selector="publish-panel-submit-button"
            />
          )}
          {publishingStatus === 'published' && (
            <PrimaryButton
              icon={IconCheckmarkCircleFill}
              size="sm"
              state="default"
              disabled={true}
              text="Published"
              className="h-8 w-full"
              data-qa-selector="publish-panel-submit-button"
            />
          )}

          {showRepublishHint && (
            <p className="text-center text-[10px] text-neutral-400">
              Republish to apply the updated domain or slug.
            </p>
          )}
          {!isPublished && (
            <p className="text-center text-[10px] text-neutral-400">
              Publish the page to the set URL.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
